import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Badge, Button, Form, Modal, Spinner, Table } from 'react-bootstrap'
import moment from 'moment'
import InputMask from 'react-input-mask'
import axios from 'axios'
import io from 'socket.io-client'

import { getApiErrorMessage, searchPatientAdmissionByAn, searchPatientAdmissionsByHn, searchPatientsForRegister, searchPatientVisitsByHn, unwrapList } from '../api'
import { calculateAge } from '../model'
import { ProjectCheckbox } from '../components'
import { PageSizeSelect } from 'components/Select/DashboardSelect'
import { DASHBOARD_SEQUENCE_COLUMN_WIDTH } from 'views/panels/accounting/dashboard/tableConstants'
import { DESKTOP_OFFLINE_MESSAGE, DESKTOP_SERVICE_URL, ensureDesktopService } from 'utility/desktopService.util'

const SEARCH_FIELDS = [
  { value: 'hn', label: 'HN', minLength: 2, placeholder: 'กรอก HN' },
  { value: 'firstName', label: 'ชื่อ', minLength: 2, placeholder: 'กรอกชื่อ' },
  { value: 'lastName', label: 'นามสกุล', minLength: 2, placeholder: 'กรอกนามสกุล' },
  { value: 'fullName', label: 'ชื่อ - นามสกุล', minLength: 2, placeholder: 'กรอกชื่อ และนามสกุล' },
  { value: 'aliasName', label: 'ชื่อเล่น', minLength: 2, placeholder: 'กรอกชื่อเล่น' },
  { value: 'cId', label: 'หมายเลขบัตรประชาชน', minLength: 4, placeholder: 'กรอกหมายเลขบัตรประชาชน' },
  { value: 'passportNumber', label: 'Passport', minLength: 4, placeholder: 'กรอกเลข Passport' },
  { value: 'phoneNumber', label: 'เบอร์โทรศัพท์', minLength: 4, placeholder: 'กรอกเบอร์โทรศัพท์' },
  { value: 'nmdHn', label: 'NMD HN', minLength: 2, placeholder: 'กรอก NMD HN' },
]
const PAGE_SIZE_OPTIONS = [5, 10, 25]
const CITIZEN_ID_MASK = '9-9999-99999-99-9'
const SEARCH_DEBOUNCE_MS = 500

const getSearchKey = (field, value) => (field === 'cId' ? value.replace(/\D/g, '') : value).trim()
const isSearchReady = (field, key) => {
  const selectedField = SEARCH_FIELDS.find((item) => item.value === field)
  return selectedField && (field === 'cId' ? key.length === 13 : key.length >= selectedField.minLength)
}

const RESULT_COLUMNS = [
  { key: 'sequence', label: 'ลำดับ', width: DASHBOARD_SEQUENCE_COLUMN_WIDTH, value: (patient, index, page, perPage) => (page - 1) * perPage + index + 1 },
  { key: 'hn', label: 'HN', width: '135px', value: (patient) => patient.hn },
  { key: 'prefixName', label: 'คำนำหน้า', width: '120px', value: (patient) => patient.person?.prefixName },
  { key: 'firstName', label: 'ชื่อ', width: '160px', value: (patient) => patient.person?.firstName },
  { key: 'lastName', label: 'นามสกุล', width: '160px', value: (patient) => patient.person?.lastName },
  { key: 'birthDate', label: 'วันเดือนปีเกิด', width: '145px', value: (patient) => patient.person?.birthDate ? moment(patient.person.birthDate).format('DD/MM/YYYY') : '' },
  { key: 'age', label: 'อายุ', width: '85px', value: (patient) => {
    const age = calculateAge(patient.person?.birthDate)
    return age ? `${age.years} ปี` : ''
  } },
]

const SearchDebugVisibility = ({ as: Component = 'div', fieldKey, label, isDebugMode, isVisible, onVisibilityChange, children, className = '', style = {} }) => {
  if (!isDebugMode && !isVisible) return null
  return (
    <Component className={className} style={{ ...style, ...(isDebugMode && !isVisible ? { opacity: 0.45 } : {}) }}>
      {isDebugMode && (
        <ProjectCheckbox
          className="patient-register-debug-visibility"
          checked={isVisible}
          title={'แสดง ' + label}
          ariaLabel={'แสดง ' + label}
          onClick={(event) => event.stopPropagation()}
          onChange={(checked) => onVisibilityChange(fieldKey, checked)}
        />
      )}
      {children}
    </Component>
  )
}

const PatientSearchModal = ({ show, onHide, onSelect, visitMode = false, admitMode = false, embedded = false, isDebugMode = false, isDebugUiVisible = () => true, onDebugUiVisibilityChange = () => {} }) => {
  const [field, setField] = useState('hn')
  const [searchKey, setSearchKey] = useState('')
  const [patients, setPatients] = useState([])
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [isScanning, setIsScanning] = useState(false)
  const [error, setError] = useState('')
  const [hoveredRow, setHoveredRow] = useState(null)
  const [selectedPatient, setSelectedPatient] = useState(null)
  const [visits, setVisits] = useState([])
  const [admissions, setAdmissions] = useState([])
  const [visitPage, setVisitPage] = useState(1)
  const [visitPerPage, setVisitPerPage] = useState(10)
  const [visitLoading, setVisitLoading] = useState(false)
  const [visitError, setVisitError] = useState('')
  const requestId = useRef(0)
  const visitRequestId = useRef(0)
  const searchTimerRef = useRef(null)
  const scanAbortRef = useRef(null)
  const scanSocketRef = useRef(null)
  const searchFields = SEARCH_FIELDS
  const activeSearchField = searchFields.find((item) => item.value === field)
  const visibleFields = searchFields.filter((item) => isDebugUiVisible(`searchHn.field.${item.value}`))
  const visibleColumns = RESULT_COLUMNS.filter((item) => isDebugMode || isDebugUiVisible(`searchHn.column.${item.key}`))

  const cancelScan = useCallback(() => {
    scanAbortRef.current?.abort()
    scanSocketRef.current?.disconnect()
    scanAbortRef.current = null
    scanSocketRef.current = null
    setIsScanning(false)
  }, [])

  useEffect(() => {
    if (!isDebugMode && !visibleFields.some((item) => item.value === field)) {
      setField(visibleFields[0]?.value || '')
    }
  }, [field, isDebugMode, visibleFields])

  useEffect(() => {
    if (show) return undefined
    cancelScan()
    clearTimeout(searchTimerRef.current)
    requestId.current += 1
    visitRequestId.current += 1
    setField('hn')
    setSearchKey('')
    setPatients([])
    setPage(1)
    setTotal(0)
    setIsLoading(false)
    setError('')
    setSelectedPatient(null)
    setVisits([])
    setAdmissions([])
    setVisitPage(1)
    setVisitLoading(false)
    setVisitError('')
    return undefined
  }, [show, cancelScan])

  useEffect(() => () => {
    clearTimeout(searchTimerRef.current)
    scanAbortRef.current?.abort()
    scanSocketRef.current?.disconnect()
  }, [])

  const loadVisits = async (patient) => {
    const hn = String(patient?.hn || '').trim()
    if (!hn) {
      setVisitError('ไม่พบ HN ของผู้ป่วยที่เลือก')
      return
    }
    const currentRequest = ++visitRequestId.current
    setSelectedPatient(patient)
    setVisitLoading(true)
    setVisitError('')
    setVisits([])
    setVisitPage(1)
    try {
      const response = await searchPatientVisitsByHn(hn)
      if (currentRequest !== visitRequestId.current) return
      const rows = unwrapList(response).filter((visit) => visit?.vn)
      rows.sort((a, b) => String(b.visitDate || '').localeCompare(String(a.visitDate || '')) || String(b.visitTime || '').localeCompare(String(a.visitTime || '')))
      setVisits(rows)
    } catch (visitSearchError) {
      if (currentRequest !== visitRequestId.current) return
      setVisitError(getApiErrorMessage(visitSearchError))
    } finally {
      if (currentRequest === visitRequestId.current) setVisitLoading(false)
    }
  }

  const loadAdmissions = async (patient) => {
    const hn = String(patient?.hn || '').trim()
    if (!hn) {
      setVisitError('ไม่พบ HN ของผู้ป่วยที่เลือก')
      return
    }
    const currentRequest = ++visitRequestId.current
    setSelectedPatient(patient)
    setVisitLoading(true)
    setVisitError('')
    setAdmissions([])
    setVisitPage(1)
    try {
      const response = await searchPatientAdmissionsByHn(hn)
      if (currentRequest !== visitRequestId.current) return
      const rows = unwrapList(response).filter((admission) => admission?.an)
      rows.sort((a, b) => String(b.checkInDate || '').localeCompare(String(a.checkInDate || '')) || String(b.checkInTime || '').localeCompare(String(a.checkInTime || '')))
      setAdmissions(rows)
    } catch (admissionSearchError) {
      if (currentRequest !== visitRequestId.current) return
      setVisitError(getApiErrorMessage(admissionSearchError))
    } finally {
      if (currentRequest === visitRequestId.current) setVisitLoading(false)
    }
  }

  const search = async (nextPage = 1, nextPerPage = perPage, nextField = field, nextSearchKey = searchKey) => {
    const key = getSearchKey(nextField, nextSearchKey)
    if (!isSearchReady(nextField, key)) return

    const currentRequest = ++requestId.current
    setIsLoading(true)
    setError('')
    try {
      const response = await searchPatientsForRegister(nextField, key, nextPage, nextPerPage)
      if (currentRequest !== requestId.current) return
      setPatients(unwrapList(response))
      setTotal(response?.data?.pagination?.total || 0)
      setPage(nextPage)
    } catch (searchError) {
      if (currentRequest !== requestId.current) return
      setPatients([])
      setTotal(0)
      setPage(1)
      setError(getApiErrorMessage(searchError))
    } finally {
      if (currentRequest === requestId.current) setIsLoading(false)
    }
  }

  const scheduleSearch = (nextField, nextSearchKey, nextPerPage = perPage) => {
    clearTimeout(searchTimerRef.current)
    const key = getSearchKey(nextField, nextSearchKey)
    if (!isSearchReady(nextField, key)) return
    searchTimerRef.current = setTimeout(() => search(1, nextPerPage, nextField, nextSearchKey), SEARCH_DEBOUNCE_MS)
  }

  const clearResults = () => {
    clearTimeout(searchTimerRef.current)
    requestId.current += 1
    visitRequestId.current += 1
    setPatients([])
    setTotal(0)
    setPage(1)
    setIsLoading(false)
    setError('')
    setSelectedPatient(null)
    setVisits([])
    setAdmissions([])
    setVisitPage(1)
    setVisitLoading(false)
    setVisitError('')
  }

  const scanCitizenCard = async () => {
    if (isScanning) return
    const controller = new AbortController()
    scanAbortRef.current = controller
    setIsScanning(true)
    setError('')

    const ready = await ensureDesktopService({ signal: controller.signal })
    if (controller.signal.aborted) return
    if (!ready.ok) {
      setError(DESKTOP_OFFLINE_MESSAGE)
      cancelScan()
      return
    }

    const socket = io(DESKTOP_SERVICE_URL)
    scanSocketRef.current = socket
    const handleError = (message) => {
      if (controller.signal.aborted) return
      cancelScan()
      setError(message || 'อ่านบัตรประชาชนไม่สำเร็จ')
    }
    const handleCardData = (data) => {
      if (controller.signal.aborted) return
      if (data?.status === 'error') {
        handleError(data?.message)
        return
      }
      const citizenId = String(data?.data?.cid || '').replace(/\D/g, '')
      if (!citizenId) return
      cancelScan()
      setField('cId')
      setSearchKey(citizenId)
      clearResults()
      scheduleSearch('cId', citizenId)
    }

    socket.on('connect', async () => {
      if (controller.signal.aborted) return
      socket.emit('register', { uuid: socket.id, connectionId: socket.id })
      try {
        await axios.post(`${DESKTOP_SERVICE_URL}/smart-cards`)
      } catch (error) {
        handleError('ไม่สามารถอ่านบัตรประชาชนได้')
      }
    })
    socket.on('scan-smart-card', handleCardData)
    socket.on('smart-card::inserted', handleCardData)
    socket.on('smart-card::error', (data) => handleError(data?.message))
    socket.on('smart-card::device-deactivated', (data) => handleError(data?.message))
    socket.on('connect_error', () => handleError(DESKTOP_OFFLINE_MESSAGE))
    socket.on('disconnect', () => handleError(DESKTOP_OFFLINE_MESSAGE))
  }

  const lastPage = Math.max(1, Math.ceil(total / perPage))
  const firstRecord = total ? (page - 1) * perPage + 1 : 0
  const lastRecord = Math.min(page * perPage, total)
  const records = admitMode ? admissions : visits
  const visitLastPage = Math.max(1, Math.ceil(records.length / visitPerPage))
  const visitFirstRecord = records.length ? (visitPage - 1) * visitPerPage + 1 : 0
  const visitLastRecord = Math.min(visitPage * visitPerPage, records.length)
  const visibleRecords = records.slice((visitPage - 1) * visitPerPage, visitPage * visitPerPage)
  const selectPatient = (patient) => admitMode ? loadAdmissions(patient) : visitMode ? loadVisits(patient) : onSelect(patient)
  const selectRecord = async (record) => {
    if (!admitMode) {
      onSelect({ ...record, hn: record.hn || selectedPatient?.hn, patient: record.patient || selectedPatient })
      return
    }
    if (visitLoading) return
    const currentRequest = ++visitRequestId.current
    setVisitLoading(true)
    setVisitError('')
    try {
      const response = await searchPatientAdmissionByAn(record.an)
      if (currentRequest !== visitRequestId.current) return
      const admission = response?.data?.data?.inPatientAdmit || unwrapList(response)[0]
      if (String(admission?.an) !== String(record.an)) throw new Error('ข้อมูล AN ที่เลือกไม่ตรงกับข้อมูลที่ค้นพบ')
      onSelect({ ...record, ...admission, an: record.an, vn: record.vn || admission.vn, hn: record.hn || admission.hn || selectedPatient?.hn, patient: admission.patient || selectedPatient })
    } catch (admissionSearchError) {
      if (currentRequest === visitRequestId.current) setVisitError(getApiErrorMessage(admissionSearchError))
    } finally {
      if (currentRequest === visitRequestId.current) setVisitLoading(false)
    }
  }
  const visitDate = (visit) => visit.visitDate && moment(visit.visitDate).isValid() ? moment(visit.visitDate).add(543, 'years').format('DD/MM/YYYY') : '-'
  const visitTime = (visit) => visit.visitTime ? String(visit.visitTime).slice(0, 5) : visit.visitDateTime ? moment(visit.visitDateTime).format('HH:mm') : '-'
  const doctorName = (visit) => visit.doctor?.person?.fullName || visit.doctor?.fullName || visit.doctor?.name || '-'
  const canceledAt = (visit) => visit.canceledAt && moment(visit.canceledAt).isValid() ? moment(visit.canceledAt).add(543, 'years').format('DD/MM/YYYY HH:mm') : '-'
  const admissionDate = (admission) => admission.checkInDate && moment(admission.checkInDate).isValid() ? moment(admission.checkInDate).add(543, 'years').format('DD/MM/YYYY') : '-'
  const admissionTime = (admission) => admission.checkInTime ? String(admission.checkInTime).slice(0, 5) : '-'
  const dischargeDate = (admission) => admission.checkOutDate && moment(admission.checkOutDate).isValid() ? moment(admission.checkOutDate).add(543, 'years').format('DD/MM/YYYY') : '-'
  const dischargeTime = (admission) => admission.checkOutTime ? String(admission.checkOutTime).slice(0, 5) : '-'
  const tableMinWidth = visibleColumns.reduce((width, column) => width + Number.parseInt(column.width, 10), 0)
  const searchInputProps = {
    'aria-label': 'คำค้นหาผู้ป่วย',
    placeholder: activeSearchField?.placeholder,
    value: searchKey,
    onChange: (event) => { setSearchKey(event.target.value); clearResults(); scheduleSearch(field, event.target.value) },
    autoFocus: true,
  }

  const content = (
    <>
      <Modal.Body>
        <style>{`
          .psm-search-grid { align-items: center; column-gap: 8px; display: grid; grid-template-columns: 156px minmax(0, 1fr); }
          .psm-search-grid > .psm-search-label { text-align: right; white-space: nowrap; }
          .psm-search-grid > .psm-search-hint { grid-column: 2 / 3; }
          .psm-type-grid { column-gap: 8px; display: grid; grid-template-columns: 156px minmax(0, 1fr); }
          .psm-type-grid > .psm-type-list { grid-column: 2 / 3; }
          @media (max-width: 991.98px) {
            .psm-search-grid, .psm-type-grid { grid-template-columns: minmax(0, 1fr); }
            .psm-search-grid > .psm-search-label { margin-bottom: 4px !important; text-align: left; }
            .psm-search-grid > .psm-search-hint, .psm-type-grid > .psm-type-list { grid-column: 1 / 2; }
          }
        `}</style>
        <Form onSubmit={(event) => event.preventDefault()}>
          <SearchDebugVisibility fieldKey="searchHn.search" label="ช่องค้นหา HN" isDebugMode={isDebugMode} isVisible={isDebugUiVisible('searchHn.search')} onVisibilityChange={onDebugUiVisibilityChange}>
            <Form.Group className="mb-2 psm-search-grid">
              <Form.Label className="mb-0 psm-search-label">{activeSearchField?.label}</Form.Label>
              <div className="d-flex align-items-center" style={{ gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {field === 'cId' ? (
                    <InputMask mask={CITIZEN_ID_MASK} maskChar="" {...searchInputProps}>
                      {(inputProps) => <Form.Control {...inputProps} />}
                    </InputMask>
                  ) : <Form.Control {...searchInputProps} />}
                </div>
                {field === 'cId' && (
                  <Button type="button" variant="outline-primary" className="mb-0" style={{ whiteSpace: 'nowrap' }} onClick={isScanning ? cancelScan : scanCitizenCard} aria-label={isScanning ? 'ยกเลิกการสแกนบัตรประชาชน' : 'สแกนบัตรประชาชน'}>
                    {isScanning ? <Spinner animation="border" size="sm" className="mr-1" /> : <i className="feather icon-credit-card mr-1" />}
                    {isScanning ? 'ยกเลิกการสแกน' : 'เสียบบัตรประชาชน'}
                  </Button>
                )}
                <Button type="button" variant="outline-secondary" className="mb-0" style={{ whiteSpace: 'nowrap' }} aria-label="ล้างการค้นหา" onClick={() => { cancelScan(); setSearchKey(''); clearResults() }}><i className="feather icon-refresh-cw mr-1" />ล้างค้นหา</Button>
              </div>
              <Form.Text className="text-muted mb-0 psm-search-hint">{field === 'cId' ? 'กรอกเลขบัตรประชาชนให้ครบ 13 หลัก ระบบจะค้นหาอัตโนมัติ' : 'เลือกประเภทและกรอกคำค้น ระบบจะค้นหาอัตโนมัติ'}</Form.Text>
            </Form.Group>
          </SearchDebugVisibility>
          <div className="psm-type-grid">
            <div className="row mb-3 psm-type-list">
              {(isDebugMode ? searchFields : visibleFields).map((item) => (
                <SearchDebugVisibility key={item.value} className="col-4 d-flex align-items-center" fieldKey={`searchHn.field.${item.value}`} label={item.label} isDebugMode={isDebugMode} isVisible={isDebugUiVisible(`searchHn.field.${item.value}`)} onVisibilityChange={onDebugUiVisibilityChange}>
                  <Form.Check
                    type="radio"
                    name="patient-register-search-field"
                    id={`patient-register-search-${item.value}`}
                    label={item.label}
                    checked={field === item.value}
                    onChange={() => { cancelScan(); setField(item.value); clearResults(); scheduleSearch(item.value, searchKey) }}
                  />
                </SearchDebugVisibility>
              ))}
            </div>
          </div>
        </Form>
        {error && <Alert variant="danger">{error}</Alert>}
        {(visitMode || admitMode) && selectedPatient ? (
          <div>
            <h6 className="mb-2">รายการ {admitMode ? 'AN' : 'VN'}</h6>
            <div className="d-flex align-items-center flex-wrap mb-2" style={{ gap: 8 }}>
              <span>ผู้ป่วย</span>
              <Form.Control readOnly aria-label="HN ผู้ป่วยที่เลือก" value={selectedPatient.hn || ''} style={{ maxWidth: 220 }} />
              <Form.Control readOnly aria-label="ชื่อผู้ป่วยที่เลือก" value={[selectedPatient.person?.prefixName, selectedPatient.person?.firstName, selectedPatient.person?.lastName].filter(Boolean).join(' ')} style={{ maxWidth: 320 }} />
            </div>
            <Button variant="outline-warning" size="sm" className="mb-2" onClick={() => { visitRequestId.current += 1; setSelectedPatient(null); setVisits([]); setAdmissions([]); setVisitLoading(false); setVisitError('') }}><i className="feather icon-arrow-left mr-1" />กลับไปยังค้นหาผู้ป่วย</Button>
            {visitError && <Alert variant="danger">{visitError} <Button variant="outline-danger" size="sm" onClick={() => admitMode ? loadAdmissions(selectedPatient) : loadVisits(selectedPatient)}>ลองใหม่</Button></Alert>}
            <div className="position-relative bg-white" style={{ border: '1px solid #dfeaf3', borderRadius: 12, boxShadow: '0 4px 14px rgba(33, 77, 122, 0.05)', overflow: 'hidden' }}>
              {visitLoading && <div className="position-absolute d-flex align-items-center justify-content-center" style={{ backgroundColor: 'rgba(255, 255, 255, 0.75)', inset: 0, zIndex: 5 }}><Spinner animation="border" variant="primary" /></div>}
              <div className="table-responsive" style={{ minHeight: 320 }}>
                <Table size="sm" className="mb-0">
                  <thead><tr>{(admitMode ? ['AN', 'วันที่รับเข้า', 'เวลา', 'วันที่ Discharge', 'เวลา Discharge', 'VN'] : ['VN', 'วันที่มา', 'เวลา', 'แพทย์', 'สถานะ']).map((label) => <th key={label} style={{ backgroundColor: '#e6f0f8', borderBottom: '2px solid #bfd6e8', color: '#38546d', fontWeight: 700, padding: '8px 10px', textAlign: 'left', textTransform: 'none' }}>{label}</th>)}</tr></thead>
                  <tbody>
                    {visibleRecords.map((record, index) => {
                      const id = admitMode ? record.an : record.vn
                      const cellStyle = { backgroundColor: hoveredRow === id ? '#d9f3ee' : index % 2 === 0 ? '#fff' : '#f8fbfe', padding: '8px 10px', textAlign: 'left', transition: 'background-color 0.15s ease' }
                      return <tr key={id} role="button" tabIndex={0} aria-label={`เลือก ${admitMode ? 'AN' : 'VN'} ${id}`} onClick={() => selectRecord(record)} onKeyDown={(event) => { if (event.key === 'Enter') selectRecord(record) }} onMouseEnter={() => setHoveredRow(id)} onMouseLeave={() => setHoveredRow(null)} style={{ cursor: 'pointer' }}><td style={cellStyle}>{id}</td><td style={cellStyle}>{admitMode ? admissionDate(record) : visitDate(record)}</td><td style={cellStyle}>{admitMode ? admissionTime(record) : visitTime(record)}</td>{admitMode && <><td style={cellStyle}>{dischargeDate(record)}</td><td style={cellStyle}>{dischargeTime(record)}</td></>}<td style={cellStyle}>{admitMode ? record.vn || '-' : doctorName(record)}</td>{!admitMode && <td style={cellStyle}><Badge variant={record.canceledAt ? 'danger' : 'success'}>{record.canceledAt ? 'ยกเลิก' : 'ปกติ'}</Badge>{record.canceledAt && record.canceledNote && <span className="ml-2">{record.canceledNote}</span>}{record.canceledAt && <small className="d-block text-muted mt-1">{canceledAt(record)}</small>}</td>}</tr>
                    })}
                    {!visibleRecords.length && !visitLoading && <tr><td colSpan={admitMode ? 6 : 5} className="text-center py-5 text-muted">No records to display</td></tr>}
                  </tbody>
                </Table>
              </div>
              <div className="d-flex align-items-center justify-content-between flex-wrap px-3 py-2" style={{ backgroundColor: '#fbfdff', borderTop: '1px solid #e9f0f6' }}>
                <strong style={{ fontSize: 14 }}>รวม {records.length} รายการ</strong>
                <div className="d-flex align-items-center">
                  <PageSizeSelect options={PAGE_SIZE_OPTIONS} value={visitPerPage} onChange={(size) => { setVisitPerPage(size); setVisitPage(1) }} />
                  <small className="mr-3 text-muted">{visitFirstRecord}-{visitLastRecord} จาก {records.length}</small>
                  <Button variant="light" size="sm" className="mr-1" disabled={visitLoading || visitPage <= 1} onClick={() => setVisitPage(1)}>«</Button>
                  <Button variant="light" size="sm" className="mr-1" disabled={visitLoading || visitPage <= 1} onClick={() => setVisitPage(visitPage - 1)}>‹</Button>
                  <span className="mx-2">{visitPage}/{visitLastPage}</span>
                  <Button variant="light" size="sm" className="mr-1" disabled={visitLoading || visitPage >= visitLastPage} onClick={() => setVisitPage(visitPage + 1)}>›</Button>
                  <Button variant="light" size="sm" disabled={visitLoading || visitPage >= visitLastPage} onClick={() => setVisitPage(visitLastPage)}>»</Button>
                </div>
              </div>
            </div>
          </div>
        ) : (
        <div className="position-relative bg-white" style={{ border: '1px solid #dfeaf3', borderRadius: 12, boxShadow: '0 4px 14px rgba(33, 77, 122, 0.05)', overflow: 'hidden' }}>
          {isLoading && <div className="position-absolute d-flex align-items-center justify-content-center" style={{ backgroundColor: 'rgba(255, 255, 255, 0.75)', inset: 0, zIndex: 5 }}><Spinner animation="border" variant="primary" /></div>}
          <div className="table-responsive" style={{ minHeight: 320 }}>
          <Table size="sm" className="mb-0" style={{ minWidth: tableMinWidth, tableLayout: 'fixed', width: '100%' }}>
            <colgroup>{visibleColumns.map((column) => <col key={column.key} style={column.key === 'sequence' ? { width: column.width } : undefined} />)}</colgroup>
            <thead style={{ backgroundColor: '#e6f0f8', color: '#38546d', fontSize: 14 }}><tr>{visibleColumns.map((column) => (
              <SearchDebugVisibility key={column.key} as="th" className={column.key === 'sequence' ? 'text-center' : ''} style={{ backgroundColor: '#e6f0f8', borderBottom: '2px solid #bfd6e8', color: '#38546d', fontWeight: 700, padding: '8px 10px', whiteSpace: 'nowrap', ...(column.key === 'sequence' ? { maxWidth: column.width, width: column.width } : {}) }} fieldKey={`searchHn.column.${column.key}`} label={column.label} isDebugMode={isDebugMode} isVisible={isDebugUiVisible(`searchHn.column.${column.key}`)} onVisibilityChange={onDebugUiVisibilityChange}>
                {column.label}
              </SearchDebugVisibility>
            ))}</tr></thead>
            <tbody>
              {patients.map((patient, index) => {
                return (
                  <tr key={patient.hn} onClick={() => selectPatient(patient)} onKeyDown={(event) => { if (event.key === 'Enter') selectPatient(patient) }} onMouseEnter={() => setHoveredRow(patient.hn)} onMouseLeave={() => setHoveredRow(null)} tabIndex={0} role="button" aria-label={`เลือกผู้ป่วย HN ${patient.hn}`} style={{ backgroundColor: hoveredRow === patient.hn ? '#d9f3ee' : index % 2 === 0 ? '#fff' : '#f8fbfe', cursor: 'pointer', transition: 'background-color 0.15s ease' }}>
                    {visibleColumns.map((column) => <td key={column.key} className={column.key === 'sequence' ? 'text-center' : ''} style={{ opacity: isDebugMode && !isDebugUiVisible(`searchHn.column.${column.key}`) ? 0.45 : 1, overflow: 'hidden', padding: '8px 10px', textOverflow: column.key === 'sequence' ? 'clip' : 'ellipsis', whiteSpace: 'nowrap' }} title={String(column.value(patient, index, page, perPage) || '')}>{column.value(patient, index, page, perPage)}</td>)}
                  </tr>
                )
              })}
              {!patients.length && !isLoading && <tr><td colSpan={visibleColumns.length || 1} className="text-center py-5 text-muted">No records to display</td></tr>}
            </tbody>
          </Table>
          </div>
          <div style={{ backgroundColor: '#fbfdff', borderTop: '1px solid #e9f0f6' }}>
            <SearchDebugVisibility className="d-flex align-items-center justify-content-between flex-wrap px-3 py-2" fieldKey="searchHn.pagination" label="แบ่งหน้าผลลัพธ์" isDebugMode={isDebugMode} isVisible={isDebugUiVisible('searchHn.pagination')} onVisibilityChange={onDebugUiVisibilityChange}>
              <strong style={{ fontSize: 14 }}>รวม {total} รายการ</strong>
              <div className="d-flex align-items-center">
                <PageSizeSelect options={PAGE_SIZE_OPTIONS} value={perPage} onChange={(size) => { setPerPage(size); if (total) search(1, size); else scheduleSearch(field, searchKey, size) }} />
                <small className="mr-3 text-muted">{firstRecord}-{lastRecord} จาก {total}</small>
                <Button variant="light" size="sm" className="mr-1" disabled={isLoading || page <= 1} onClick={() => search(1)}>«</Button>
                <Button variant="light" size="sm" className="mr-1" disabled={isLoading || page <= 1} onClick={() => search(page - 1)}>‹</Button>
                <span className="mx-2">{page}/{lastPage}</span>
                <Button variant="light" size="sm" className="mr-1" disabled={isLoading || page >= lastPage} onClick={() => search(page + 1)}>›</Button>
                <Button variant="light" size="sm" disabled={isLoading || page >= lastPage} onClick={() => search(lastPage)}>»</Button>
              </div>
            </SearchDebugVisibility>
          </div>
        </div>
        )}
      </Modal.Body>
      <Modal.Footer><Button variant="outline-secondary" onClick={onHide}><i className="feather icon-x mr-1" />ปิด</Button></Modal.Footer>
    </>
  )

  if (embedded) return content

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable aria-labelledby="patient-register-search-title">
      <Modal.Header closeButton>
        <Modal.Title id="patient-register-search-title">{admitMode ? 'ค้นหา AN ผู้ป่วย' : visitMode ? 'ค้นหา VN ผู้ป่วย' : 'ค้นหา HN ผู้ป่วย'}</Modal.Title>
      </Modal.Header>
      {content}
    </Modal>
  )
}

export default PatientSearchModal
